/**
 * Zone Edit Dialog - New Implementation
 *
 * Create/edit zones with direct field values.
 * Simplified without language dictionary.
 */
import React, { useState, useEffect } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	TextField,
	Box,
	Typography,
	FormControl,
	FormLabel,
	FormControlLabel,
	Checkbox,
	Select,
	MenuItem,
	InputLabel,
} from "@mui/material";

// Predefined zone type options
const ZONE_TYPE_OPTIONS = ["Zone", "Trailer", "Dolly"];

/**
 * ZoneEditDialog - Modal for creating/editing zones
 */
const ZoneEditDialog = ({
	open,
	onClose,
	onSave,
	zone,
	existingZones = [],
	layout,
	inspectionTypes = [],
}) => {
	const [formData, setFormData] = useState({
		tag: "",
		tagType: "Zone",
		tagNumber: "",
		assetViewLocation: [0, 0],
		inspectionTypes: [],
	});
	const [initialFormData, setInitialFormData] = useState(null);

	useEffect(() => {
		if (zone) {
			// Editing existing zone - use direct field values
			// Ensure tagType is a valid option, default to "Zone" if not
			const validTagType = zone.tagType && ZONE_TYPE_OPTIONS.includes(zone.tagType)
				? zone.tagType
				: "Zone";
			const data = {
				tag: zone.tag || "",
				tagType: validTagType,
				tagNumber: zone.tagNumber ?? "",
				assetViewLocation: zone.assetViewLocation || [0, 0],
				inspectionTypes: zone.inspectionTypes || [],
			};
			setFormData(data);
			setInitialFormData(data);
		} else {
			// Creating new zone - auto-generate tag number
			const maxTagNumber = existingZones.reduce(
				(max, z) => Math.max(max, z.tagNumber || 0),
				0
			);
			const data = {
				tag: "",
				tagType: "Zone",
				tagNumber: maxTagNumber + 1,
				assetViewLocation: [0, 0],
				inspectionTypes: [],
			};
			setFormData(data);
			setInitialFormData(data);
		}
	}, [zone, open, existingZones]);

	const handleChange = (field, value) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
	};

	const handleLocationChange = (index, value) => {
		const newLocation = [...formData.assetViewLocation];
		newLocation[index] = value === "" ? "" : parseInt(value, 10) || 0;
		setFormData((prev) => ({ ...prev, assetViewLocation: newLocation }));
	};

	const handleInspectionTypeToggle = (detailType, typeId) => {
		const currentTypes = formData.inspectionTypes || [];
		// Check if already selected (could be stored as detailType or typeId)
		const isSelected = currentTypes.includes(detailType) ||
			(typeId && currentTypes.includes(typeId));

		if (isSelected) {
			// Remove both possible values to ensure clean state
			setFormData((prev) => ({
				...prev,
				inspectionTypes: currentTypes.filter(
					(id) => id !== detailType && id !== typeId
				),
			}));
		} else {
			// Add using detailType as the canonical ID
			setFormData((prev) => ({
				...prev,
				inspectionTypes: [...currentTypes, detailType],
			}));
		}
	};

	// Get inspection name - DIRECT field access
	const getInspectionName = (inspection) => {
		return inspection.detailType || "Unknown Inspection";
	};

	const handleSave = () => {
		const zoneData = {
			tag: formData.tag,
			tagType: formData.tagType,
			tagNumber: formData.tagNumber,
			assetViewLocation: formData.assetViewLocation.map((val) => (val === "" ? 0 : val)),
			inspectionTypes: formData.inspectionTypes,
		};

		onSave(zoneData);
		onClose();
	};

	const isFormValid = () => {
		const tagNum = Number(formData.tagNumber);
		return (
			formData.tag.trim() !== "" &&
			formData.tagType &&
			formData.tagType.trim() !== "" &&
			String(formData.tagNumber).trim() !== "" &&
			!isNaN(tagNum) &&
			tagNum >= 0
		);
	};

	const isDirty = () => {
		if (!initialFormData) return false;
		return (
			formData.tag !== initialFormData.tag ||
			formData.tagType !== initialFormData.tagType ||
			String(formData.tagNumber) !== String(initialFormData.tagNumber) ||
			formData.assetViewLocation[0] !== initialFormData.assetViewLocation[0] ||
			formData.assetViewLocation[1] !== initialFormData.assetViewLocation[1] ||
			JSON.stringify(formData.inspectionTypes.slice().sort()) !==
				JSON.stringify(initialFormData.inspectionTypes.slice().sort())
		);
	};

	// Get grid dimensions from layout
	const gridWidth = layout?.assetViewGrid?.[0] || 7;
	const gridHeight = layout?.assetViewGrid?.[1] || 7;

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>{zone ? "Edit Zone" : "Add New Zone"}</DialogTitle>
			<DialogContent>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
					<TextField
						label="Zone Name"
						value={formData.tag}
						onChange={(e) => handleChange("tag", e.target.value)}
						fullWidth
						placeholder="e.g., Engine, Cabin, Driver Side Door"
						helperText="Enter the zone name"
						required
					/>

					<FormControl fullWidth required>
						<InputLabel id="zone-type-label">Tag Type</InputLabel>
						<Select
							labelId="zone-type-label"
							label="Tag Type"
							value={formData.tagType}
							onChange={(e) => handleChange("tagType", e.target.value)}
						>
							{ZONE_TYPE_OPTIONS.map((typeOption) => (
								<MenuItem key={typeOption} value={typeOption}>
									{typeOption}
								</MenuItem>
							))}
						</Select>
					</FormControl>

					<TextField
						label="Zone Number"
						type="number"
						value={formData.tagNumber}
						onChange={(e) => handleChange("tagNumber", e.target.value)}
						fullWidth
						helperText="Numeric identifier for this zone"
						inputProps={{ min: 0 }}
						required
					/>

					<FormControl component="fieldset" sx={{ mt: 2 }}>
						<FormLabel component="legend">Inspection Types</FormLabel>
						<Box
							sx={{
								maxHeight: 200,
								overflowY: "auto",
								mt: 1,
								display: "flex",
								flexWrap: "wrap",
								columnGap: 3,
								rowGap: 0.5,
							}}
						>
							{[...inspectionTypes]
								.sort((a, b) =>
									getInspectionName(a)
										.toLowerCase()
										.localeCompare(getInspectionName(b).toLowerCase())
								)
								.map((inspection) => {
									// Use detailType as the canonical ID since it's always available
									const id = inspection.detailType;
									const name = getInspectionName(inspection);
									// Check for match against detailType or typeId (for backwards compatibility)
									const currentTypes = formData.inspectionTypes || [];
									const checked = currentTypes.includes(id) ||
										(inspection.typeId && currentTypes.includes(inspection.typeId));

									return (
										<FormControlLabel
											key={id}
											control={
												<Checkbox
													checked={checked}
													onChange={() => handleInspectionTypeToggle(id, inspection.typeId)}
												/>
											}
											label={name}
											sx={{
												mr: 0,
												alignItems: "center",
												"& .MuiFormControlLabel-label": {
													whiteSpace: "nowrap",
												},
											}}
										/>
									);
								})}
						</Box>
					</FormControl>

					{zone && (
						<>
							<Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
								Grid Position
							</Typography>
							<Box sx={{ display: "flex", gap: 2 }}>
								<TextField
									label="X Position"
									type="number"
									value={formData.assetViewLocation[0]}
									onChange={(e) => handleLocationChange(0, e.target.value)}
									fullWidth
									inputProps={{ min: 0, max: gridWidth - 1 }}
									helperText={`0 to ${gridWidth - 1}`}
								/>
								<TextField
									label="Y Position"
									type="number"
									value={formData.assetViewLocation[1]}
									onChange={(e) => handleLocationChange(1, e.target.value)}
									fullWidth
									inputProps={{ min: 0, max: gridHeight - 1 }}
									helperText={`0 to ${gridHeight - 1}`}
								/>
							</Box>
						</>
					)}
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Cancel</Button>
				<Button
					onClick={handleSave}
					variant="contained"
					color="primary"
					disabled={!isFormValid() || !isDirty()}
				>
					{zone ? "Save Changes" : "Add Zone"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ZoneEditDialog;
